import { useState } from 'react';
import { Badge, Button, Modal } from '@/components/UI';
import { showToast } from '@/components/Layout/Toast';
import type { OptimizerResultRow } from './types';
import { formatDecimal, formatNullablePct01, formatPct01, formatSignedPct, profitColorClass, toNumber } from './helpers';

interface RuleJsonModalProps {
    result: OptimizerResultRow | null;
    selectedJobUsesOptionEv: boolean;
    selectedJobUsesOptionMetric: boolean;
    onClose: () => void;
}

const RuleJsonModal = ({ result, selectedJobUsesOptionEv, selectedJobUsesOptionMetric, onClose }: RuleJsonModalProps) => {
    const [showRuleJsonRawState, setShowRuleJsonRawState] = useState<{ resultId: number | null; visible: boolean }>({
        resultId: null,
        visible: false
    });

    const resultId = result?.id ?? null;
    const showRuleJsonRaw = showRuleJsonRawState.resultId === resultId ? showRuleJsonRawState.visible : false;

    const handleClose = () => {
        setShowRuleJsonRawState({ resultId: null, visible: false });
        onClose();
    };

    return (
        <Modal isOpen={result !== null} size="xl" title="Optimizer Result Rule" onClose={handleClose}>
            {result && (
                <>
                    <div className="text-zinc-400 text-sm mb-3">
                        resultId={result.id} jobId={result.jobId}
                    </div>

                    <div className="flex items-center gap-2 mb-3">
                        <Badge variant="secondary">
                            score=
                            {result.selectionScore !== null ? formatDecimal(result.selectionScore, 4) : '?'}
                        </Badge>
                        <Badge variant="secondary">cond={result.conditionCount}</Badge>
                        <Badge variant="secondary">depth={result.depth}</Badge>
                    </div>

                    <div className="text-white text-sm font-semibold break-words mb-2">{result.computedRuleSummary}</div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-xs text-zinc-400">
                        <div>
                            Sweet: <span className="text-white">{formatDecimal(result.sweetScore, 3)}</span>
                        </div>
                        <div>
                            Lift: <span className={profitColorClass(result.lift)}>{formatDecimal(result.lift, 2)}%</span>
                        </div>
                        <div>
                            Coverage: <span className="text-white">{formatPct01(result.coverage, 1)}</span>
                        </div>
                        <div>
                            {selectedJobUsesOptionEv ? 'EV Win' : 'Win Rate'}:{' '}
                            <span className="text-white">{formatPct01(result.winRate, 1)}</span>
                        </div>
                        {selectedJobUsesOptionMetric && (
                            <>
                                <div>
                                    Executed Opt Win: <span className="text-white">{formatNullablePct01(result.optionWinRate, 1)}</span>
                                </div>
                                <div>
                                    Opt Exec: <span className="text-white">{formatNullablePct01(result.optionExecutionRate, 1)}</span>
                                </div>
                                <div>
                                    Avg Exec Opt:{' '}
                                    <span className={profitColorClass(result.avgOptionProfit)}>
                                        {result.avgOptionProfit !== null ? formatSignedPct(toNumber(result.avgOptionProfit)) : '-'}
                                    </span>
                                </div>
                                <div>
                                    Median Exec Opt:{' '}
                                    <span className={profitColorClass(result.medianOptionProfit)}>
                                        {result.medianOptionProfit !== null ? formatSignedPct(toNumber(result.medianOptionProfit)) : '-'}
                                    </span>
                                </div>
                            </>
                        )}
                        {result.holdoutLift !== null && (
                            <div>
                                HO Lift:{' '}
                                <span className={profitColorClass(result.holdoutLift)}>{formatDecimal(result.holdoutLift, 2)}%</span>
                            </div>
                        )}
                        {result.holdoutSweetScore !== null && (
                            <div>
                                HO Sweet: <span className="text-white">{formatDecimal(result.holdoutSweetScore, 3)}</span>
                            </div>
                        )}
                        {result.holdoutWinRate !== null && (
                            <div>
                                HO Win Rate: <span className="text-white">{formatPct01(result.holdoutWinRate, 1)}</span>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-between mb-2">
                        <div className="text-zinc-500 text-sm">Rule JSON</div>
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setShowRuleJsonRawState({ resultId, visible: !showRuleJsonRaw })}
                            >
                                {showRuleJsonRaw ? 'Hide JSON' : 'Show JSON'}
                            </Button>
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                    if (!navigator.clipboard) {
                                        showToast('Clipboard API not available', 'warning');
                                        return;
                                    }

                                    navigator.clipboard
                                        .writeText(result.ruleJson)
                                        .then(() => showToast('Copied rule JSON', 'success'))
                                        .catch(() => showToast('Failed to copy', 'error'));
                                }}
                            >
                                Copy JSON
                            </Button>
                        </div>
                    </div>

                    {showRuleJsonRaw && (
                        <pre className="bg-zinc-950 text-zinc-200 text-xs p-3 rounded overflow-auto max-h-[60vh] whitespace-pre-wrap">
                            {result.ruleJson}
                        </pre>
                    )}
                </>
            )}
        </Modal>
    );
};

export default RuleJsonModal;
